import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Label } from '@pixacare/domain';
import { FlattenedLabel } from '@pixacare/dtos';
import { catchError, concatMap, map, mergeMap, Observable, switchMap } from 'rxjs';
import { LabelHttpService } from 'src/app/services/http/label.http.service';
import { lifeCycleActions } from '../life-cycle/life-cycle.actions';
import { notificationsActions } from '../notifications/notifications.actions';
import { usersActions } from '../users/users.actions';
import { labelsActions } from './labels.actions';

@Injectable()
export class LabelsEffect {
  private readonly action$ = inject(Actions);
  private readonly labelService = inject(LabelHttpService);
  private readonly translocoService = inject(TranslocoService);

  getUserLabels$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(labelsActions.getUserLabels),
      mergeMap(() =>
        this.labelService.getFlattenedLabels().pipe(
          concatMap((flattenedLabels: FlattenedLabel[]) => [
            usersActions.updateUsers({
              users: flattenedLabels.map(({ createdBy }) => createdBy).filter((user) => !!user),
            }),
            labelsActions.getUserLabelsSuccess({
              flattenedLabels,
            }),
          ]),
          catchError((error: Error) => [lifeCycleActions.loadError({ error })]),
        ),
      ),
    ),
  );

  createLabels$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(labelsActions.createLabels),
      mergeMap(({ labels }) =>
        this.labelService.createLabel(labels).pipe(
          switchMap(() => [
            labelsActions.getUserLabels(),
            notificationsActions.success({
              title: this.translocoService.translate('Les mots clés ont bien été ajoutés.'),
              message: this.translocoService.translate('Mots clés créés'),
            }),
          ]),
          catchError((error: Error) => [
            lifeCycleActions.loadError({ error }),
            notificationsActions.error({
              error,
              title: this.translocoService.translate(`Une erreur est survenue.`),
              message: this.translocoService.translate("Nous n'avons pas pu créer les mots clés."),
            }),
          ]),
        ),
      ),
    ),
  );

  unsubscribeFromLabel$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(labelsActions.unsubscribeFromLabel),
      mergeMap(({ labelId, removeFromSequences }) =>
        this.labelService.unsubscribeFromLabel(labelId, removeFromSequences).pipe(
          map(() => labelsActions.unsubscribeFromLabelSuccess({ labelId })),
          catchError((error: Error) => [lifeCycleActions.loadError({ error })]),
        ),
      ),
    ),
  );

  // ---- Label packs ---

  createLabelPackLabels$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(labelsActions.createLabelPackLabels),
      mergeMap(({ labelPackId, clientCode, departmentId, labels }) =>
        this.labelService.createLabelPackLabels(labelPackId, clientCode, labels).pipe(
          switchMap((createdLabels: Label[]) => [
            labelsActions.addLabels({
              labels: createdLabels,
            }),
            labelsActions.createLabelPackLabelsSuccess({
              labelPackId,
              clientCode,
              departmentId,
              labelIds: createdLabels.map((label) => label.id),
            }),
            notificationsActions.success({
              title: this.translocoService.translate('Les mots clés ont bien été ajoutés.'),
              message: this.translocoService.translate('Mots clés créés'),
            }),
          ]),
          catchError((error: Error) => [
            lifeCycleActions.loadError({ error }),
            notificationsActions.error({
              error,
              title: this.translocoService.translate(`Une erreur est survenue.`),
              message: this.translocoService.translate("Nous n'avons pas pu créer les mots clés."),
            }),
          ]),
        ),
      ),
    ),
  );

  deleteLabelPackLabels$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(labelsActions.deleteLabelPackLabels),
      mergeMap(({ labelPackId, clientCode, departmentId, labels }) =>
        this.labelService
          .deleteLabelPackLabels(
            labelPackId,
            clientCode,
            labels.map((label) => label.word),
          )
          .pipe(
            switchMap(() => [
              labelsActions.deleteLabelPackLabelsSuccess({
                labelPackId,
                clientCode,
                departmentId,
                labelIds: labels.map((label) => label.id),
              }),
              notificationsActions.success({
                title: this.translocoService.translate('Le mot clé a bien été supprimé.'),
                message: this.translocoService.translate('Mot clé supprimé'),
              }),
            ]),
            catchError((error: Error) => [
              lifeCycleActions.loadError({ error }),
              notificationsActions.error({
                error,
                title: this.translocoService.translate(`Une erreur est survenue.`),
                message: this.translocoService.translate("Nous n'avons pas pu supprimer le mot clé."),
              }),
            ]),
          ),
      ),
    ),
  );
}
